<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" @click.stop>
        <slot></slot>
      </div>
    </template>

    <v-card variant="flat">
      <v-card-title class="d-flex justify-space-between">
        <span>{{$t('routes.pod.menuAccesslink.createShareLink')}}</span>
        <v-btn icon @click.stop="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" color="primary" show-arrows>
        <v-tab>{{ $t('routes.pod.menuAccesslink.shareLinkInformation') }}</v-tab>
        <v-tab>{{ $t('routes.pod.menuAccesslink.emailInvitations') }}</v-tab>
        <v-tab>{{ $t('routes.pod.menuAccesslink.options') }}</v-tab>
      </v-tabs>

      <v-card-text ref="cardText" class="scrollable">
        <v-form ref="form" :disabled="isFormDisabled" autocomplete="off">
          <v-window v-model="tab" style="background-color: unset !important;">
            <v-window-item class="pt-4">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="linkName"
                    type="text"
                    :label="$t('routes.pod.menuAccesslink.name')"
                    :rules="linkNameRules"
                    variant="solo-filled"
                    block
                    flat
                    hide-details="auto"
                    data-cy="podMenu-tools-manageGenericLinks-name"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    type="text"
                    :label="$t('routes.pod.menuAccesslink.password')"
                    v-model="password"
                    :rules="passwordRules"
                    variant="solo-filled"
                    required
                    block
                    flat
                    hide-details
                    data-cy="podMenu-tools-manageGenericLinks-password"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip v-if="canAccessClipboard && password && password.length > 0" location="bottom" :z-index="1200" :aria-label="$t('routes.pod.menuAccesslink.copyPassword')">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            @click="copyPasswordToClipboard"
                            :aria-label="$t('routes.pod.menuAccesslink.copyPassword')"
                            variant="text"
                            density="compact"
                            size="32"
                            icon
                          >
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('routes.pod.menuAccesslink.copyPassword') }}</span>
                      </v-tooltip>
                      <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('routes.pod.menuAccesslink.generatePassword')">
                        <template v-slot:activator="{props}">
                          <v-btn
                            v-bind="props"
                            @click="generatePassword"
                            :aria-label="$t('routes.pod.menuAccesslink.generatePassword')"
                            variant="text"
                            density="compact"
                            size="32"
                            icon
                          >
                            <v-icon>mdi-lock-reset</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('routes.pod.menuAccesslink.generatePassword') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-date-input
                    v-model="expiryDate"
                    :label="$t('routes.pod.menuAccesslink.expiryDate')"
                    :min="getMinDatepicker"
                    show-adjacent-months
                    rounded="md"
                    hide-details="auto"
                    data-cy="podMenu-tools-manageGenericLinks-datePicker"
                    append-inner-icon="mdi-calendar"
                    prepend-icon=""
                    variant="solo-filled"
                    flat
                    required
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div data-cy="podMenu-tools-manageGenericLinks-notes">
                    <MdEditor
                      v-model="linkNotes"
                      :theme="$vuetify.theme.name === 'dark' ? 'dark' : 'light'"
                      :preview="false"
                      :placeholder="$t('routes.pod.menuAccesslink.notes')"
                      :aria-label="$t('routes.pod.menuAccesslink.notes')"
                      noUploadImg
                      :toolbars="[
                        'bold', 'italic', 'strikeThrough', 'title', '-', 'code', 'unorderedList',
                        'orderedList', '-', 'link', 'image', 'table', '-', 'previewOnly']"
                      :footers="[]"
                      language="en-US"
                      style="height: 220px;"
                    />
                  </div>
                </v-col>
              </v-row>
              <v-row class="mt-4">
                <v-col v-if="orderedNodes.length > 0" cols="12">
                  <h4>{{ $t('routes.pod.menuAccesslink.selectedFolders') }}</h4>
                  <p v-if="someSelectedFolderHasChildren" class="font-italic my-3">
                    {{ $t('routes.pod.menuAccesslink.onlyOneLevelOfChildrenFolderAccessibleLine1') }}
                    <br />
                    {{ $t('routes.pod.menuAccesslink.onlyOneLevelOfChildrenFolderAccessibleLine2') }}
                  </p>
                  <v-list density="compact">
                    <v-list-item v-for="node in orderedNodes" :key="node.id">
                      {{ getNodePath(node) }}
                      <template v-slot:append>
                        <v-btn
                          :aria-label="$t('routes.pod.menuAccesslink.removeFolder', { name: this.getNodePath(node) })"
                          icon="mdi-close"
                          width="28px"
                          height="28px"
                          @click="removeNode(node)"
                          variant="text"
                        />
                      </template>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col v-if="orderedPods.length > 0" cols="12">
                  <h4>{{ $t('routes.pod.menuAccesslink.selectedFiles') }}: </h4>
                  <v-list density="compact">
                    <v-list-item v-for="pod in orderedPods" :key="pod.id">
                      {{ getPodPath(pod) }}
                      <template v-slot:append>
                        <v-btn
                          :aria-label="$t('routes.pod.menuAccesslink.removeFile', { name: this.getPodPath(pod) })"
                          icon="mdi-close"
                          width="28px"
                          height="28px"
                          @click="removePod(pod)"
                          variant="text"
                        />
                      </template>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col v-if="pods.length === 0 && nodes.length === 0" class="text-center bg-error" style="padding: 4px 10px">
                  {{ $t('routes.node.noFileOrFolderSelected') }}
                </v-col>
              </v-row>
              <v-row>
                <v-col class="text-center">
                  <div class="d-inline-block">
                    <SelectPodsDialog
                      :browseNodeId="rootNode.id"
                      @selected-pods="addPodByIds"
                      @selected-nodes="addNodeByIds"
                    >
                      <v-btn color="primary" elevation="0">
                        {{ $t('routes.node.addFilesOrFolders') }}
                      </v-btn>
                    </SelectPodsDialog>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <v-window-item class="pt-4">
              <div>
                <v-form ref="emailsForm" class="mt-4" @submit.prevent="addEmailRecipient">
                  <v-text-field
                    v-model="addEmailInput"
                    :rules="addEmailInputRules"
                    :label="$t('routes.pod.menuAccesslink.emailAddresses')"
                    :placeholder="$t('routes.pod.menuAccesslink.enterEmailAddressAndPress')"
                    flat
                  >
                    <template v-slot:append-inner>
                      <v-btn height="32" density="default" variant="elevated" v-show="addEmailInput" @click="addEmailRecipient">
                        <v-icon>mdi-plus-circle</v-icon>
                        {{$t('common.add')}}
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-form>

                <div v-if="emailRecipientsList.length > 0" class="mb-4 vb-invited-recipients">
                  <v-chip
                    v-for="email in emailRecipientsList"
                    color="primary"
                    variant="flat"
                    @click:close="removeEmailRecipient(email)"
                    :key="email"
                    close-icon="mdi-close"
                    :close-label="$t('routes.pod.menuAccesslink.removeEmailRecipient', { email })"
                    :text="email"
                    closable
                  />
                </div>
              </div>

              <div>
                <v-textarea
                  v-model="emailNote"
                  :label="$t('menus.menuUploadLink.noteToEmail')"
                  variant="solo-filled"
                  rows="3"
                  class="mt-3"
                  flat
                />
              </div>
              <div>
                <v-checkbox
                  v-model="includeQrCode"
                  :label="$t('routes.pod.menuAccesslink.addQrCodeToEmail')"
                  hide-details
                />
              </div>
            </v-window-item>

            <v-window-item class="pt-4">
              <v-row>
                <v-col cols="12">
                  <div v-if="isTeamMemberUser" data-cy="podMenu-tools-manageGenericLinks-teamOnly">
                    <v-checkbox
                      v-if="$isTeamUser()"
                      class="mt-0"
                      v-model="teamOnly"
                      :disabled="isTeamonlyDisabled || isFormDisabled"
                      :label="$t('routes.pod.menuAccesslink.teamOnly')"
                      hide-details
                      density="compact"
                    />
                    <div v-if="isTeamonlyDisabled">
                      {{ $t('routes.pod.menuAccesslink.teamonlyMustBeChecked') }}
                    </div>
                  </div>

                  <div data-cy="podMenu-tools-manageGenericLinks-allowDownloading">
                    <v-checkbox
                      v-model="allowDownloading"
                      :label="$t('routes.pod.menuAccesslink.allowDownloading')"
                      hide-details
                      block
                      density="compact"
                    />
                  </div>

                  <div data-cy="podMenu-tools-manageGenericLinks-allowComments">
                    <v-checkbox
                      v-model="allowComments"
                      :disabled="isCommentsDisabled || isFormDisabled"
                      :label="$t('routes.pod.menuAccesslink.allowComments')"
                      hide-details
                      block
                      density="compact"
                    />
                    <div v-if="showCommentsErrorMessage" class="pl-1">
                      {{ $t('routes.pod.menuAccesslink.commentsAreNotAllowed') }}
                    </div>
                  </div>

                  <div data-cy="podMenu-tools-manageGenericLinks-showAssets">
                    <v-checkbox
                      v-model="showAssets"
                      :label="$t('routes.pod.menuAccesslink.showAssets')"
                      hide-details
                      block
                      density="compact"
                    />
                  </div>

                  <div data-cy="podMenu-tools-manageGenericLinks-showSubtitles">
                    <v-checkbox
                      v-model="showSubtitles"
                      :label="$t('routes.pod.menuAccesslink.showSubtitles')"
                      :disabled="!!showAssets || isFormDisabled"
                      hide-details
                      block
                      density="compact"
                    />
                  </div>

                  <div data-cy="podMenu-tools-manageGenericLinks----">
                    <v-checkbox
                      v-model="allowPlayingRaw"
                      :label="$t('routes.pod.menuAccesslink.allowPlayingRaw')"
                      density="compact"
                      hide-details
                      block
                    />
                  </div>

                  <div class="pb-4" data-cy="podMenu-tools-manageGenericLinks-addVersions">
                    <v-checkbox
                      v-model="addVersions"
                      :label="$t('routes.pod.menuAccesslink.addVersions')"
                      density="compact"
                      hide-details
                      block
                    />
                  </div>
                </v-col>
              </v-row>
            </v-window-item>
          </v-window>
        </v-form>

        <div v-if="updatedLink">
          <div class="d-flex mt-4">
            <div class="flex-grow-1">
              <span id="link-url">URL</span>

              <v-text-field
                readonly
                variant="solo-filled"
                :model-value="getAccesslinkURL"
                aria-labelledby="link-url"
                hide-details
                flat
              />
            </div>
            <div v-if="canAccessClipboard" class="flex-grow-0 pt-5 pl-2">
              <v-tooltip location="bottom" :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    type="button"
                    @click="copyUrlToClipboard()"
                    variant="text"
                    :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                    rounded
                    tile
                    icon
                    data-cy="podMenu-tools-embedMediaPreview-copyUrl-btn"
                  >
                    <v-icon tile>mdi-clipboard-file-outline</v-icon>
                  </v-btn>
                </template>

                {{ $t('routes.pod.menuEmbedMedia.copyToClipboard') }}
              </v-tooltip>
            </div>
          </div>

          <div
            v-if="updatedLink?.invitations && Array.isArray(updatedLink.invitations) && updatedLink.invitations.length > 0"
            class="mt-4"
          >
            <span class="font-weight-bold">{{ $t('routes.pod.menuAccesslink.invitedPeople') }}:</span>
            {{ updatedLink.invitations.map((i) => i.email).join(', ') }}
          </div>
        </div>

        <v-row>
          <v-col cols="12">
            <div v-show="successMessage" class="inline-message--success mt-1" role="status" data-cy="podMenu-tools-">{{ successMessage }}</div>
            <div v-show="errorMessage" class="inline-message--error mt-1" role="status" data-cy="podMenu-tools-">{{ errorMessage }}</div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="createShareLink" :disabled="isFormDisabled" color="primary" variant="flat" elevation=0 data-cy="dialog--ok">
          {{ $t('common.create') }}
        </v-btn>
        <v-btn @click.stop="dialog = false" variant="text" data-cy="dialog--false">
          {{ updatedLink ? $t('common.close') : $t('common.cancel') }}
        </v-btn>
      </v-card-actions>

    </v-card>

  </v-dialog>
</template>

<script>
import { MdEditor } from 'md-editor-v3';
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useTenantStore } from '@/stores/TenantStore.js';
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import SelectPodsDialog from '../../../pods2/components/dialogs/SelectPodsDialog.vue';

import MimetypeHelper from '../../../../components/MimetypeHelper.js';
import Helper from '@/components/helper';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { addDays } from 'date-fns/addDays';
import { set as dateSet } from 'date-fns/set';
import { formatDate, formatDateTimeMilliseconds } from '@/components/formatters.js';
import { UTCDate } from '@date-fns/utc';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: "AccesslinkCreateDialog",
  components: {
    SelectPodsDialog,
    MdEditor,
  },
  props: {
    rootNode: {
      type: Object,
      required: true,
    },
    selectedPods: {
      type: Array,
      required: true,
    },
    selectedNodes: {
      type: Array,
      required: true,
    },
    setAllowComments: {
      type: Boolean,
      required: false,
    },
    setNotifyViaEmail: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      tab: 0,

      pods: [],
      nodes: [],

      linkName: "",
      linkNameRules: [
        (v) => !v || v.trim().length > 0 || this.$t('routes.pod.menuAccesslink.nameWhitespaceOnly'),
        (v) => !v || v.trim().length <= 191 || this.$t('routes.pod.menuAccesslink.nameMaxLength'),
      ],
      password: "",
      passwordRules: [
        (v) => {
          const value = v ? v.trim() : v;
          let message = null;

          if (value) {
            if (value.length < 8) {
              message = this.$t('menus.createUser.passwordLength');
            } else if (!Helper.isValidPassword(value)) {
              message = this.$t('menus.createUser.passwordInvalidChars');
            }
          }

          return message ?? true;
        },
      ],

      datepickerMenu: false,
      expiryDateValue: new Date(),
      expiryDateRules: [
        v => !!v || this.$t('routes.pod.menuAccesslink.expiryDateRequired')
      ],

      showAssets: false,
      showSubtitles: false,
      allowDownloading: true,
      allowComments: false,
      includeQrCode: false,
      teamOnly: false,
      allowPlayingRaw: false,
      addVersions: false,

      linkNotes: "",

      addEmailInput: "",
      addEmailInputRules: Helper.getEmailValidationRules(this, { required: false }),

      emailRecipientsList: [],
      emailNote: '',

      errorMessage: "",
      successMessage: "",

      updatedLink: null,

      simplemdeConfigs: {
        spellChecker: false,
        placeholder: this.$t('routes.pod.menuAccesslink.notes'),
        toolbar: [
          'bold', 'italic', 'strikethrough', 'heading', '|',
          'code', 'unordered-list', 'ordered-list', '|',
          'link', 'image', 'table', '|',
          'preview', 'guide'
        ],
        status: false,
      }
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
    isTeamonlyDisabled(newValue) {
      if (newValue) {
        this.teamOnly = true;
      }
    },
    isCommentsDisabled(newValue) {
      if (newValue) {
        this.allowComments = false;
      }
    },
    updatedLink(newValue) {
      if (newValue) {
        const cardText = this.$refs.cardText.$el;

        this.$nextTick(() => {
          cardText.scroll({
            top: cardText.scrollHeight,
            behavior: 'smooth',
          });
        });
      }
    },
    showAssets(newValue) {
      if (newValue) {
        this.showSubtitles = false;
      }
    }
  },
  mounted() {
    // The tenant settings are obtained in the MainLayout.

    // Initialize password rules after component is mounted
    this.initializePasswordRules();
  },
  methods: {
    initializePasswordRules() {
      this.passwordRules = [
        (v) => {
          const value = v ? v.trim() : v;

          if (value) {
            if (value.length < 8) {
              return this.$t('routes.pod.menuAccesslink.passwordMinLength');
            } else if (value.length > 191) {
              return this.$t('routes.pod.menuAccesslink.passwordMaxLength');
            } else if (!Helper.isValidPassword(value)) {
              return this.$t('menus.createUser.passwordInvalidChars');
            }
          }

          return true;
        }
      ];
    },
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.resetFormData();
      });
    },
    resetFormData() {
      this.tab = 0;
      this.pods = this.selectedPods.map(x => x);
      this.nodes = this.selectedNodes.map(x => x);

      this.linkName = '';
      this.password = '';
      this.datepickerMenu = false;

      const tenantExpiryDays = this.getTenantLinksExpiryDays;
      this.expiryDate = formatDate(addDays(new UTCDate(), tenantExpiryDays));

      const tenantStore = useTenantStore();
      this.showAssets = tenantStore.getLinksShowAssets;
      this.showSubtitles = !this.showAssets && tenantStore.getLinksShowSubtitles;
      this.allowDownloading = tenantStore.getLinksAllowDownloading;
      this.allowComments = tenantStore.getLinksAllowComments;
      this.includeQrCode = false;
      this.allowPlayingRaw = tenantStore.getLinksAllowPlayingOriginalFile;
      this.addVersions = tenantStore.getLinksAddVersions;
      this.teamOnly = this.isTeamonlyDisabled;

      this.linkNotes = "";
      this.addEmailInput = "";

      this.emailRecipientsList = [];
      this.emailNote = '';

      this.errorMessage = "";
      this.successMessage = "";

      this.updatedLink = null;

      if (this.isCommentsDisabled) {
        this.allowComments = false;
      } else if (this.setAllowComments) {
        this.allowComments = true;
      }

      if (this.setNotifyViaEmail) {
        // this.notifyViaEmail = true;
      }
    },
    async createShareLink() {
      this.errorMessage = "";
      this.successMessage = "";

      const validForm = await this.$refs.form.validate();
      if (!validForm.valid || (this.pods.length === 0 && this.nodes.length === 0)) {
        return;
      }

      const linkName = this.linkName.trim();
      this.linkName = linkName;

      let formData = {
        podids: this.pods.map(x => x.id),
        nodeids: this.nodes.map(x => x.id),
        password: this.password,
        note: this.linkNotes,
        showassets: this.showAssets ? 1 : 0,
        showsubtitles: this.showSubtitles ? 1 : 0,
        allowdownloading: this.allowDownloading ? 1 : 0,
        allowcomments: this.allowComments ? 1 : 0,
        teamonly: this.teamOnly,
        allowplayingraw: this.allowPlayingRaw ? 1 : 0,
        addversions: this.addVersions ? 1 : 0,
        // Send the date in UTC
        expireat: formatDateTimeMilliseconds(dateSet(new UTCDate(this.expiryDate), {
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 0,
        })),
      };

      if (linkName) {
        formData.name = linkName;
      }

      if (this.isCommentsDisabled) {
        formData.allowcomments = 0;
      }

      const accesslinkStore = useAccesslinkStore();
      let VERB = accesslinkStore.createAccesslink;
      let self = this;

      if (this.accesslink) {
        formData.id = this.accesslink.id;
        VERB = accesslinkStore.updateAccesslink;
      }

      VERB(formData)
        .then(function (accesslink) {
          if (self.emailRecipientsList.length > 0) {
            return self.sendInvitations(accesslink).then((links) => {
              if (links.length > 0) {
                return links.find(x => x.id === accesslink.id);
              }

              return accesslink;
            });
          } else {
            return accesslink;
          }
        })
        .then(function (accessLink) {
          self.updatedLink = accessLink;
          accesslinkStore.refreshAccesslinks(self.rootNode.id);
          self.successMessage = self.$t('routes.pod.menuAccesslink.linkCreated');
        })
        .catch(function (error) {
          if (error?.response?.data?.error) {
            self.errorMessage = self.$localizeMessage(error.response.data.error);
          } else {
            self.errorMessage = self.$t('backend.system-error');
            console.log(error?.response?.data); // unhandled error
          }
        });
    },
    addPodByIds(podIds) {
      for (const aPodId of podIds) {
        const isAdded = this.pods.find(x => x.id === aPodId);
        if (!isAdded) {
          this.pods.push(this.getPodById(aPodId));
        }
      }
    },
    addNodeByIds(nodeIds) {
      for (const aNodeId of nodeIds) {
        const isAdded = this.nodes.find(x => x.id === aNodeId);
        if (!isAdded) {
          this.nodes.push(this.getNodeById(aNodeId));
        }
      }
    },
    removePod: function (pod) {
      const idx = this.pods.indexOf(pod);
      console.assert(idx >= 0, `Couldn't find pod with id ${pod.id} within this.pods`);
      this.pods.splice(idx, 1);
    },
    removeNode: function (node) {
      const idx = this.nodes.indexOf(node);
      console.assert(idx >= 0, `Couldn't find node with id ${node.id} within this.nodes`);
      this.nodes.splice(idx, 1);
    },
    generatePassword: function () {
      const passwordLength = 10;
      this.password = Helper.generatePassword(passwordLength);
    },
    async addEmailRecipient() {
      const validForm = await this.$refs.emailsForm.validate();
      if (!validForm.valid || !this.addEmailInput) {
        return;
      }

      if (this.emailRecipientsList.indexOf(this.addEmailInput) >= 0) {
        this.addEmailInput = "";
        return;
      }

      this.emailRecipientsList.push(this.addEmailInput);
      this.addEmailInput = "";
    },
    removeEmailRecipient(email) {
      this.emailRecipientsList = this.emailRecipientsList.filter(x => x !== email);
    },
    async sendInvitations(accesslink) {
      const payload = {
        linkId: accesslink.id,
        receiver: this.emailRecipientsList.join(','),
        note: this.emailNote,
        includeqrcode: this.includeQrCode ? 1 : 0,
      };

      const self = this;

      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.inviteAccesslink(payload)
        .then(function (response) {
          self.emailRecipientsList = [];
          self.emailNote = '';

          return Array.isArray(response?.links) ? response.links : [];
        });
    },
    copyUrlToClipboard: function() {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(this.getAccesslinkURL)
      }
    },
    copyPasswordToClipboard: function() {
      if (this.password.length > 0) {
        navigator.clipboard.writeText(this.password)
      }
    },
    getPodPath: function (pod) {
      const podNodeName = pod.nodeid ? this.getNodeById(pod.nodeid)?.name : null;
      if (podNodeName) {
        return `${podNodeName} / ${pod.name}`;
      }

      return pod.name
    },
    getNodePath: function (node) {
      const nodeParentName = node.parentid ? this.getNodeById(node.parentid)?.name : null;
      if (nodeParentName) {
        return `${nodeParentName} / ${node.name}`;
      }

      return node.name;
    }
  },
  computed: {
    expiryDate: {
      get: function () {
        return formatDate(this.expiryDateValue);
      },
      set: function (value) {
        this.expiryDateValue = value;
      }
    },
    getMinDatepicker: function () {
      return formatDate(new Date());
    },
    getTenantLinksExpiryDays() {
      const tenantStore = useTenantStore();
      return tenantStore.getTenantLinksExpiryDays;
    },
    getPodById: function () {
      const podStore = usePodStore();
      return (podId) => {
        return podStore.getPodById(podId);
      }
    },
    isFormDisabled() {
      return (this.pods.length === 0 && this.nodes.length === 0) || this.updatedLink !== null;
    },
    getAccesslinkURL() {
      if (this.updatedLink && this.updatedLink.url) {
        return window.location.origin + this.updatedLink.url;
      }
      return '';
    },
    isTeamonlyDisabled: function () {
      // check if any of the pods has teamonly flag.
      if (Array.isArray(this.pods) && this.pods.length > 0) {
        if (this.pods.some((x) => x.teamonly)) {
          return true;
        }
      }

      // check if any of the nodes has teamonly flag.
      if (Array.isArray(this.nodes) && this.nodes.length > 0) {
        if (this.nodes.some((x) => x.teamonly)) {
          return true;
        }

        // check if any of the pods in the nodes has teamonly flag.
        for (const node of this.nodes) {
          if (node.pods && node.pods.length > 0) {
            if (node.pods.some((x) => x.teamonly)) {
              return true;
            }
          }
        }
      }

      return false;
    },
    isCommentsDisabled: function() {
      // comments are allowed only for video and audio pods
      return !this.hasVideoAudioOrImageFiles && (this.nodes.length === 0);
    },
    isTeamMemberUser: function () {
      return this.$canControlTenantConfig() || this.$canBelongstoTeam();
    },
    getNodeById: function () {
      const nodeStore = useNodeStore();

      return (nodeId) => {
        const node = nodeStore.getNodeById(nodeId);
        if (!node) {
          nodeStore.getFullNodeInfo(nodeId);
          return null;
        }

        return node;
      }
    },
    canAccessClipboard: function() {
      // Note: this works only on localhost and HTTPS
      return window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
    },
    hasVideoAudioOrImageFiles: function() {
      return this.pods.some((x) =>  MimetypeHelper.isMimetypeVideo(x) ||
                                    MimetypeHelper.isMimetypeAudio(x) ||
                                    MimetypeHelper.isMimetypeImage(x));
    },
    showCommentsErrorMessage: function() {
      const hasPods = this.pods.length > 0;
      const hasNodes = this.nodes.length > 0;

      // comments are allowed only for video and audio pods
      // if there are folder, we will not show the error message.
      return hasPods && (!hasNodes && !this.hasVideoAudioOrImageFiles);
    },
    someSelectedFolderHasChildren: function() {
      return this.nodes.some((node) => node.subfoldersCount > 0);
    },
    orderedPods: function() {
      return [...this.pods].sort((a, b) => {
        if (a.nodeid === b.nodeid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.nodeid).name.localeCompare(this.getNodeById(b.nodeid).name);
      });
    },
    orderedNodes: function() {
      return [...this.nodes].sort((a, b) => {
        if (a.parentid === b.parentid) {
          return a.name.localeCompare(b.name);
        }

        return this.getNodeById(a.parentid).name.localeCompare(this.getNodeById(b.parentid).name);
      });
    }
  }
}
</script>
